import { getParent, types, type Instance } from 'mobx-state-tree';
import MetaModel from './metamodel';
import UIModel from './uimodel';
import { DateTime } from 'luxon';
import { initializeOET } from '../../merode/commands/uimodel';
import type { IModel as IMerodeModel } from '../../merode/commands/types';

export const VERSION = '1.0.0';

// The parent of a Model in the tree is a ModelVersion (see
// `../projects/model_version`); importing it here would create an import cycle
// (ModelVersion holds `content: Model`). We only reach back for `triggerSave`,
// so a narrow structural interface is enough to break the cycle.
interface IModelParent {
  triggerSave(): void;
}

// `DateTime.toISO()` is typed `string | null` (luxon returns null for an invalid
// instant), but a freshly-created UTC DateTime is always valid, so the runtime
// value is a plain string. `types.optional(types.string, …)` needs a `() =>
// string` factory; assert the non-null at this single boundary.
const currentTime = (): string => {
  return DateTime.utc().toISO() as string;
};

/**
 * The root object of a MERODE model in Merlin.
 *
 * It contains:
 * - a metamodel: see './metamodel'
 * - a uimodel: see './uimodel'
 * - a version of the format (to allow for conditional logic based on the
 *   version in the future)
 * - a timestamp of the time of creation
 */
const Model = types
  .model('Model', {
    metamodel: types.optional(MetaModel, () => MetaModel.create({})),
    uimodel: types.optional(UIModel, () => UIModel.create({})),
    version: types.optional(types.string, () => VERSION),
    timestamp: types.optional(types.string, currentTime),
  })
  .actions((self) => ({
    afterAttach() {
      // `initializeOET` declares its param as merode's structural `IModel`
      // ({ metamodel: IMetaModel; uimodel: IUIModel }). The Model instance has
      // exactly those members, but MST types the embedded model views as
      // `readonly` arrays whereas the hand-declared interfaces use mutable ones;
      // the shapes are otherwise identical. Cast at this single call boundary.
      if (initializeOET(self as unknown as IMerodeModel)) {
        // Trigger a manual save, since this happens before the autosave is in place.
        const modelVersion = getParent<IModelParent>(self);
        modelVersion.triggerSave();
      }
    },
  }));

export type IModel = Instance<typeof Model>;

export default Model;
